import React, { FC, memo, useEffect, useMemo } from 'react';
import {
  useFocusedIds,
  useIsFocusActive,
} from '@components/MainStage/focusOn.store';
import { getListSearchState } from '@components/MainStage/listSearch.store';
import useStore from '@components/MainStage/store';
import { SideMenu } from '@components/SideMenu';
import { NodesTypes } from '@constants/canvas/general';
import { subLayersNodeIdsByMode } from '@constants/canvas/layerNodes';
import { Modes } from '@constants/canvas/layers';
import MenuNodesProvider from '@context/MenuNodesProvider';
import { useProject } from '@context/Project/ProjectProvider';
import { useProjectFilters } from '@context/ProjectFilters/ProjectFiltersProvider';
import { Box } from '@mui/material';
import { checkScopeFilter } from '@utils/domains';
import AssetsBlock from '@views/Project/components/ListMenu/components/AssetsBlock/AssetsBlock';
import FocusOnChip from '@views/Project/components/ListMenu/components/FocusOnChip';
import { LayerModeMenus } from '@views/Project/components/ListMenu/components/LayerModeMenus';
import { ListMenuHeader } from '@views/Project/components/ListMenu/components/ListMenuHeader';

type Props = {
  open: boolean;
};

export const ListMenu: FC<Props> = memo(({ open }) => {
  const { nodes } = useStore();

  const {
    isVersionsShown,
    toolbox: { domains, mode },
  } = useProject();

  const { reviewStatus, validationStatus } = useProjectFilters();

  const isFocusActive = useIsFocusActive();
  const focusedIds = useFocusedIds();

  useEffect(() => {
    getListSearchState().reset();
  }, [mode]);

  useEffect(() => () => getListSearchState().reset(), []);

  const nodesProviderValue = useMemo(() => {
    const focusedSet = new Set(focusedIds);

    return {
      nodes: mode
        ? nodes.filter(
            (node) =>
              node.type === NodesTypes.CustomNode &&
              node.parentNode &&
              subLayersNodeIdsByMode[mode].includes(node.parentNode) &&
              (!isFocusActive || focusedSet.has(node.id)) &&
              (!domains.length ||
                checkScopeFilter(
                  domains,
                  node.data?.dto?.type,
                  node.data?.dto?.domainNodes,
                )) &&
              (!reviewStatus.length || node.data?.dto?.review_required) &&
              (!validationStatus.length ||
                validationStatus.includes(node.data?.dto?.valid_status ?? '')),
          )
        : [],
    };
  }, [
    nodes,
    mode,
    domains,
    reviewStatus,
    validationStatus,
    isFocusActive,
    focusedIds,
  ]);

  return (
    <SideMenu
      side="left"
      open={open}
      sx={{ display: 'flex', flexDirection: 'column' }}
    >
      {mode === Modes.RiskManagement ? (
        <AssetsBlock />
      ) : (
        <ListMenuHeader count={nodesProviderValue.nodes.length} />
      )}

      <FocusOnChip />

      {!isVersionsShown && (
        <Box
          overflow="auto"
          flex={1}
          pb={4}
          sx={{ scrollbarGutter: 'stable both-edges' }}
        >
          <MenuNodesProvider value={nodesProviderValue}>
            <LayerModeMenus />
          </MenuNodesProvider>
        </Box>
      )}
    </SideMenu>
  );
});
